// @flow
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Input,
  Select,
  Modal,
} from 'antd';
import { SpH5, SpError, SpButton } from 'components/DesignKit';
import SimpleReactValidator from 'simple-react-validator';
import getStateList from 'utils/stateList';
import getCountryAlpha2 from 'utils/getCountryAlpha2';
import {
  validators,
} from 'utils/validationMessages';

import { COUNTRY_CODES } from 'appconstants';

const { Option } = Select;

type Props = {
  visible: boolean,
  close: Function,
  submit: Function,
  locationSelected: Object,
  submitting: boolean,
  selectedAccount: Object;
  country: String;
};

const EditLocation = (props: Props) => {
  const {
    visible,
    close,
    submit,
    locationSelected,
    submitting,
    selectedAccount,
    country,
  } = props;

  const [, forceUpdate] = useState();
  const [locationName, setLocationName] = useState(locationSelected.name);
  const [streetName, setStreetName] = useState(locationSelected.address.street);
  const [cityName, setCityName] = useState(locationSelected.address.city);
  const [stateName, setStateName] = useState(locationSelected.address.state);
  const [zipCode, setZipCode] = useState(locationSelected.address.postalCode);
  const [zipTag, setZipTag] = useState('');
  const [countryCode, setCountryCode] = useState(getCountryAlpha2(locationSelected.address.country.id));
  const [countryName, setCountryName] = useState('');
  const [stateList, setSateList] = useState([]);
  const cntryId = locationSelected.address.country.id;
  const gid = locationSelected && locationSelected.gid;
  const [disableButton, setDisableButton] = useState(false);
  const postalCodeRegex = country === 'India' ? /^[1-9][0-9]{5}$/ : /^[0-9][0-9]{4}$/;

  useEffect(() => {
    if (selectedAccount && selectedAccount.country) {
      if (selectedAccount.country.id === 1) {
        setZipTag('Zip code');
      } else if (selectedAccount.country.id === 2) {
        setZipTag('Pin code');
      }
    }
  }, [selectedAccount]);

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      postalcode: {
        message: validators.address.pincode,
        rule: val => (postalCodeRegex.test(val)),
      },
    },
  }));

  useEffect(() => {
    setSateList(getStateList(cntryId));
    if (cntryId && cntryId === 1) {
      setCountryName('USA');
    } else if (cntryId && cntryId === 2) {
      setCountryName('INDIA');
    }
  }, []);

  const updateCountry = (value) => {
    setCountryCode(value);
    setSateList(getStateList(value));
  };

  useEffect(() => {
    if (!submitting) {
      setDisableButton(false);
    }
  }, [submitting]);

  const validate = (event) => {
    setDisableButton(true);
    event.preventDefault();
    const formValid = simpleValidator.current.allValid();
    setDisableButton(formValid);
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    submit({
      locationName,
      cityName,
      zipCode,
      stateName,
      streetName,
      cntryId,
      countryCode,
      gid,
    });
  };

  return (
    <Modal
      centered
      closable={false}
      visible={visible}
      footer={[
        <SpButton onClick={() => close()} type="secondary">Cancel</SpButton>,
        <SpButton onClick={validate} disabled={disableButton}>Update</SpButton>,
      ]}
      title="Update Location"
    >
      <div className="px-4">
        <div className={submitting ? 'OVERLAY' : ''} />
        <div className="mb-3">
          <label htmlFor=""><SpH5>Name</SpH5></label>
          <Input
            placeholder="Name"
            value={locationName}
            onChange={(e) => setLocationName(e.currentTarget.value)}
          />
          <SpError>
            {simpleValidator.current.message('name', locationName, 'required')}
          </SpError>
        </div>
        <div className="mb-3">
          <label htmlFor=""><SpH5>Street Address</SpH5></label>
          <Input
            placeholder="Street Address"
            value={streetName}
            onChange={(e) => setStreetName(e.currentTarget.value)}
          />
          <SpError>
            {simpleValidator.current.message('street', streetName, 'required')}
          </SpError>
        </div>

        <div className="pb-3">
          <div className="d-flex">
            <div className="w-50 mr-3">
              <label className="d-block"><SpH5>City</SpH5></label>
              <Input
                placeholder="City"
                value={cityName}
                onChange={(e) => setCityName(e.currentTarget.value)}
              />
              <SpError>
                {simpleValidator.current.message('city', cityName, 'required')}
              </SpError>
            </div>
            <div className="w-50">
              <label className="d-block"><SpH5>State</SpH5></label>
              <Select
                className="w-100"
                showSearch
                optionFilterProp="children"
                defaultValue={stateName}
                onChange={(e) => setStateName(e)}
              >
                {
                stateList.map(s => (
                  <Option
                    key={s.id}
                    value={s.id}
                    className="text-capitalize"
                  >
                    { s.name }
                  </Option>
                ))
              }
              </Select>
              <SpError>
                {simpleValidator.current.message('state', stateName, 'required')}
              </SpError>
            </div>
          </div>
        </div>

        <div className="pb-3">
          <div className="d-flex">
            <div className="w-50 mr-3">
              <label className="d-block"><SpH5>{zipTag}</SpH5></label>
              <Input
                placeholder={zipTag}
                value={zipCode}
                onChange={(e) => setZipCode(e.currentTarget.value)}
              />
              <SpError>
                {simpleValidator.current.message('Area code', zipCode, 'required|postalcode')}
              </SpError>
            </div>
            <div className="w-50">
              <label className="d-block font-weight-bold">Country</label>
              <Select
                defaultValue={countryName}
                style={{ width: '100%' }}
                disabled
                onChange={updateCountry}
              >
                {COUNTRY_CODES.map((item) => (
                  <Option key={item.ID} value={item.ALPHA2}>
                    {item.NAME}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
});

// $FlowFixMe
export default connect(mapStateToProps, null)(EditLocation);
